import { useFormContext } from "react-hook-form";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import CustomTextInput from "@components/input/CustomTextInput";
import React, { useEffect, useState } from "react";
import FormTitle from "@pages/join/_components/FormTitle";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { IFormInput } from "@pages/findpassword/FindPasswordPage";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation } from "@tanstack/react-query";
interface EmailValidationProps {
  title?: string;
  fieldName: keyof IFormInput;
  modalMessage?: string;
  subMessage?: string;
  setStep?: (step: number) => void;
  type: "signup" | "findPassword";
}
// 공용 이메일 검증 컴포넌트
export default function EmailValidationComponent({
  title,
  fieldName,
  modalMessage,
  subMessage,
  setStep,
  type,
}: EmailValidationProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { trigger, watch, setError } = useFormContext<IFormInput>();
  const [success, setSuccess] = useState(false);
  const email = watch(fieldName);
  const language = window.localStorage.getItem("language") || "ko";
  const { postFindPasswordEmail, getCheckEmail } = useApiRequest();

  useEffect(() => {
    setSuccess(false);
  }, [email]);

  const { mutate: checkEmail } = useMutation({
    mutationFn: (email: string) => getCheckEmail(email),
    onSuccess: (data) => {
      const emailExists = data?.exists ?? false;

      if (type === "signup") {
        // 회원가입의 경우:
        // exists가 true면 -> 이미 존재하는 이메일
        // exists가 false면 -> 사용 가능한 이메일
        if (emailExists) {
          setSuccess(false);
          setError(fieldName, {
            type: "manual",
            message: "중복된 이메일 입니다.",
          });
        } else {
          setSuccess(true);
        }
      } else if (type === "findPassword") {
        // 비밀번호 찾기의 경우:
        // exists가 true면 -> 존재하는 이메일, 비밀번호 초기화 메일 발송
        // exists가 false면 -> 존재하지 않는 이메일
        if (emailExists) {
          findPasswordEmailMutation(email);
        } else {
          setError(fieldName, {
            type: "manual",
            message: "존재하지 않는 이메일입니다.",
          });
        }
      }
    },
  });

  // 비밀번호 찾기용 이메일 검증
  const { mutate: findPasswordEmailMutation } = useMutation({
    mutationFn: (email: string) => postFindPasswordEmail(email, language),
    onSuccess: () => {
      setIsModalOpen(true);
      setSuccess(true);
    },
  });

  const handleVerify = async () => {
    const isValid = await trigger(fieldName);
    if (isValid) {
      checkEmail(email);
    }
  };

  return (
    <>
      {title && <FormTitle>{title}</FormTitle>}
      <InputWrapper>
        <CustomTextInput
          name={fieldName}
          type="text"
          width="100%"
          mode="email"
          placeholder="이메일 입력"
          message={success ? "사용 가능한 이메일입니다." : ""}
          success={success}
          max={100}
        />

        <BasicButton
          title={type === "signup" ? "중복확인" : "인증하기"}
          height="5.2rem"
          width="8.3rem"
          padding="1.6rem 1.2rem"
          type="submit"
          onClick={handleVerify}
          disabled={!email}
        />
      </InputWrapper>

      {modalMessage && (
        <BasicModal
          open={isModalOpen}
          setOpen={setIsModalOpen}
          activeEvent={() => {
            setIsModalOpen(false);
          }}
          activeName="확인"
          notice={true}
          padding="2rem">
          <ModalContent
            mainMessage={modalMessage}
            subMessage={subMessage ?? ""}
          />
        </BasicModal>
      )}
    </>
  );
}

export const InputWrapper = styled.div({
  display: "flex",
  gap: "1rem",
  marginTop: "1rem",
});
